import { DEV } from 'cc/env';
import { CurveRange, _decorator } from 'cc';
import Delay from '../../Utils/Delay';
import ValuePicker from './ValuePicker';

const { ccclass, property } = _decorator;

@ccclass
export default class StepValueAccelerator extends ValuePicker {
    @property
    public maxValue: number = 0;

    @property
    public minValue: number = 0;

    @property
    public disableButtonOnLimitValue: boolean = false;

    @property
    public step: number = 0;

    @property
    public startAccelerateDelay: number = 5000;

    @property
    public maxRunngingTime: number = 0;

    @property
    public enableLoopStep: boolean = true;

    @property({ tooltip: DEV && '當前數值小於一次步進值時，往上加是否強制設置為一次步進值' })
    public stickWithStep: boolean = true;

    @property
    public enableCustomSpeedCurve: boolean = false;

    @property({
        type: CurveRange,
        visible() {
            return this.enableCustomSpeedCurve;
        },
    })
    public readonly customSpeedCurve: CurveRange = new CurveRange();

    public onLoad() {
        super.onLoad();
        this.on(ValuePicker.EVENT_TYPE.VALUE_CHANGED, this.onValueChanged, this);
    }

    protected *run(isUpward: boolean) {
        this.startRunningTime = Date.now();
        const endTime = this.startRunningTime + this.maxRunngingTime + this.startAccelerateDelay;
        const startValue = this.value;
        const targetValue = isUpward ? this.maxValue : this.minValue;
        const difference = Math.abs(targetValue - startValue);
        while (Date.now() < endTime + this.pauseDuration) {
            yield Delay.resolve(0);
            if (this.isPaused) {
                continue;
            }
            const linearIncrement = this.step * Math.trunc(Math.max(this.pastRunningTime / 100, 1));
            const timePercentage = (this.pastRunningTime - this.startAccelerateDelay) / this.maxRunngingTime;
            const powerIncrement = this._evaluateValue(timePercentage, difference);
            const increment = Math.trunc(Math.max(powerIncrement, linearIncrement));
            this.value = startValue + increment * (isUpward ? 1 : -1);
            // 當數值超過上下限則停止繼續跑動
            if (!this._validateValue()) {
                break;
            }
        }
        this.value = targetValue;
    }

    protected stepOnce(isUpward: boolean): void {
        if (this.value < this.step && this.stickWithStep && isUpward) {
            this.value = this.step;
        } else if (this.value < this.step && !this.stickWithStep && isUpward) {
            this.value += this.step;
        } else if (this.value !== this.minValue && this.value - this.step < this.minValue && !isUpward) {
            this.value = this.minValue;
        } else if (this.value === this.maxValue && isUpward) {
            if (this.enableLoopStep) {
                this.value = this.minValue;
            }
        } else if (this.value === this.minValue && !isUpward) {
            if (this.enableLoopStep) {
                this.value = this.maxValue;
            }
        } else {
            this.value += this.step * (isUpward ? 1 : -1);
            this._validateValue();
        }
    }

    protected onValueChanged() {
        if (this.disableButtonOnLimitValue) {
            this.updateInteractable();
        }
    }

    protected updateInteractable() {
        if (this.increaseButton) {
            this.increaseButton.interactable = this.interactable && (!this.disableButtonOnLimitValue || this.value < this.maxValue);
        }
        if (this.decreaseButton) {
            this.decreaseButton.interactable = this.interactable && (!this.disableButtonOnLimitValue || this.value > this.minValue);
        }
    }

    /**
     * 驗證當前值是否符合規定，若不符則調整至符合值
     * @return 是否符合規定
     */
    private _validateValue() {
        if (this.value > this.maxValue) {
            this.value = this.maxValue;
            return false;
        }
        if (this.value < this.minValue) {
            this.value = this.minValue;
            return false;
        }
        return true;
    }

    private _evaluateValue(timeProgress: number, difference: number) {
        if (this.enableCustomSpeedCurve) {
            return this.customSpeedCurve.evaluate(timeProgress) * difference;
        }
        return Math.max(timeProgress ** 7 * difference);
    }
}
